// node_modules
import { useReducer, useEffect, createContext, Dispatch } from "react";
// Controllers
import { AuthControllerSingleton } from "Controllers";
// Enums
import { AuthActionTypeEnum } from "Enums";
// Reducers
import { AuthReducer } from "Reducers";
// Types
import { TAuthDTO, TAuthAction, TAuthState } from "Types";
// Constants
import { GeneralConstants } from "Constants";

// Initial auth state of the app at the start
const initialAuthState: TAuthState = {
    antiForgeryToken: "",
    userEmail: "",
    igorAIEnv: "",
    hasAccessToDynamicDelivery: false,
    dynamicDeliveryFreeTrialDaysNumber: GeneralConstants.DYNAMIC_DELIVERY_14_DAYS_FREE_TRIAL,
    organizationName: ""
};

// Context is created using React’s createContext() hook. 
// Passing a default state and dispatch function here isn't necessary 
// since it is set after by the provider.
// But we do it anyway for better code auto-completion in the IDE.
export const AuthContext = createContext<{
    authState: TAuthState;
    authDispatch: Dispatch<TAuthAction>;
}>({
    authState: initialAuthState,
    authDispatch: () => null
});

export const AuthStore = ({children}: any) => {
    // When using hook,
    // we get access to 2 values:
    // - a current state,
    // - and a dispatch function.
    // useReducer takes 2 parameters:
    // - the state reducer,
    // - and an initial state
    const [authState, authDispatch] = useReducer(AuthReducer, initialAuthState);

    const getInitialAuthStateAsync = async (): Promise<void> => {
        const response: TAuthDTO | undefined = await AuthControllerSingleton.getAsync();
        if (response && response.antiForgeryToken && response.userEmail && response.igorAIEnv && response.dynamicDeliveryFreeTrialDaysNumber) {
            // The user successfully logged in, so update the authentication state
            authDispatch({ 
                type: AuthActionTypeEnum.InitAuthState, 
                newAntiForgeryToken: response.antiForgeryToken, 
                newUserEmail: response.userEmail,
                newIgorAIEnv: response.igorAIEnv,
                newHasAccessToDynamicDelivery: response.hasAccessToDynamicDelivery,
                newDynamicDeliveryFreeTrialDaysNumber: response.dynamicDeliveryFreeTrialDaysNumber,
                newOrganizationName: response.organizationName
            });
        } else {
            // The user is not logged in, so redirect the user to the login page with the 
            // current URL as the return URL
            window.location.href = `/account/login?returnUrl=${window.location.pathname}`;
        }
    };
    
    useEffect(() => {
        getInitialAuthStateAsync();
    }, []);

    return (
        <AuthContext.Provider value={ { authState, authDispatch } }>
            {children}
        </AuthContext.Provider>
    );
};
